// Hand-maintained — safe to edit directly.
// Admin Portal namespace for managing invitations and change requests.

import type { RequestOptions } from "../base-client";
import { RequestBuilder } from "../request-builder";

// --- Response types ---

export interface PortalInvitation {
  id: string;
  type: "portal-invitation";
  attributes: {
    email: string;
    expires_at: string;
    accepted_at: string | null;
    inserted_at: string;
    updated_at: string;
  };
  relationships?: {
    tenant?: { data: { id: string; type: string } };
    invited_by?: { data: { id: string; type: string } };
  };
}

export interface PortalChangeRequest {
  id: string;
  type: "portal-change-request";
  attributes: {
    field_name: string;
    old_value: string | null;
    new_value: string;
    status: string;
    reviewed_at: string | null;
    inserted_at: string;
    updated_at: string;
  };
  relationships?: {
    portal_user?: { data: { id: string; type: string } };
    tenant?: { data: { id: string; type: string } };
    reviewed_by?: { data: { id: string; type: string } | null };
  };
}

export function createPortalNamespace(rb: RequestBuilder) {
  return {
    invitations: {
      /**
       * List all portal invitations.
       */
      list: async (options?: RequestOptions): Promise<PortalInvitation[]> => {
        const response = await rb.rawGet<{ data: PortalInvitation[] }>(
          "/portal-invitations",
          options,
        );
        return response.data;
      },

      /**
       * Create a new portal invitation.
       * @param email - Email to invite
       * @param tenantId - Tenant to invite into
       * @param invitedById - User who is sending the invitation
       */
      create: async (
        email: string,
        tenantId: string,
        invitedById: string,
        options?: RequestOptions,
      ): Promise<PortalInvitation> => {
        const response = await rb.rawPost<{ data: PortalInvitation }>(
          "/portal-invitations",
          {
            data: {
              type: "portal-invitation",
              attributes: {
                email,
                tenant_id: tenantId,
                invited_by_id: invitedById,
              },
            },
          },
          options,
        );
        return response.data;
      },

      /**
       * Delete a portal invitation.
       * @param id - Invitation ID
       */
      delete: async (id: string, options?: RequestOptions): Promise<void> => {
        return rb.rawDelete(`/portal-invitations/${id}`, options);
      },
    },

    changeRequests: {
      /**
       * List all portal change requests.
       */
      list: async (
        options?: RequestOptions,
      ): Promise<PortalChangeRequest[]> => {
        const response = await rb.rawGet<{ data: PortalChangeRequest[] }>(
          "/portal-change-requests",
          options,
        );
        return response.data;
      },

      /**
       * Approve a change request.
       * @param id - Change request ID
       * @param reviewedById - Admin user approving
       */
      approve: async (
        id: string,
        reviewedById: string,
        options?: RequestOptions,
      ): Promise<PortalChangeRequest> => {
        const response = await rb.rawPatch<{ data: PortalChangeRequest }>(
          `/portal-change-requests/${id}/approve`,
          {
            data: {
              type: "portal-change-request",
              id,
              attributes: { reviewed_by_id: reviewedById },
            },
          },
          options,
        );
        return response.data;
      },

      /**
       * Reject a change request.
       * @param id - Change request ID
       * @param reviewedById - Admin user rejecting
       */
      reject: async (
        id: string,
        reviewedById: string,
        options?: RequestOptions,
      ): Promise<PortalChangeRequest> => {
        const response = await rb.rawPatch<{ data: PortalChangeRequest }>(
          `/portal-change-requests/${id}/reject`,
          {
            data: {
              type: "portal-change-request",
              id,
              attributes: { reviewed_by_id: reviewedById },
            },
          },
          options,
        );
        return response.data;
      },
    },
  };
}
